/* =======================================================================
   MenuSection — Pagina/Sezione Menù con categorie navigabili
   =======================================================================
   I piatti sono in src/data/menu.js. Clic su una categoria in alto →
   mostra i piatti di quella categoria. I prezzi e gli allergeni si modificano
   tutti in menu.js.
   ======================================================================= */
import { useLang } from "@/context/LanguageContext";
import { menuCategories } from "@/data/menu";
import { useState } from "react";

export default function MenuSection({ copy }) {
  const { tr } = useLang();
  const [active, setActive] = useState(menuCategories[0].id);

  const activeCat = menuCategories.find((c) => c.id === active);

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="bg-[#F7F4EB] text-[#2C2C2A] py-24 lg:py-36"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8">
            <p className="text-[0.72rem] tracking-[0.32em] uppercase text-[#737554] mb-6">
              — {tr(copy.menuSection.overline)}
            </p>
            <h2 className="font-serif font-light text-[clamp(2.4rem,5vw,4.6rem)] leading-[1.02] tracking-tight">
              {tr(copy.menuSection.title)}
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-sm font-light leading-relaxed text-[#3a3a37]">
              {tr(copy.menuSection.sub)}
            </p>
          </div>
        </div>

        {/* Category nav */}
        <div
          data-testid="menu-categories"
          className="flex flex-wrap gap-x-6 gap-y-3 mb-14 pb-6 border-b border-[#D7D0B7]"
        >
          {menuCategories.map((c) => (
            <button
              key={c.id}
              data-testid={`cat-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`text-[0.78rem] tracking-[0.18em] uppercase pb-1 transition-colors border-b-2 ${
                active === c.id
                  ? "border-[#BC472A] text-[#BC472A]"
                  : "border-transparent text-[#2C2C2A]/70 hover:text-[#2C2C2A]"
              }`}
            >
              {tr(c.title)}
            </button>
          ))}
        </div>

        {/* Active category content */}
        {activeCat && (
          <div data-testid={`active-cat-${activeCat.id}`} className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
              <p className="font-handwriting text-3xl text-[#BC472A] mb-3">
                {String(menuCategories.findIndex((c) => c.id === activeCat.id) + 1).padStart(2, "0")}
              </p>
              <h3 className="font-serif text-4xl lg:text-5xl italic leading-tight mb-5">
                {tr(activeCat.title)}
              </h3>
              <p className="text-base font-light leading-relaxed text-[#3a3a37] max-w-sm">
                {tr(activeCat.intro)}
              </p>
            </div>

            <div className="lg:col-span-8">
              <ul className="divide-y divide-[#D7D0B7]">
                {activeCat.items.map((item, i) => (
                  <li
                    key={i}
                    data-testid={`item-${activeCat.id}-${i}`}
                    className="py-6 group flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-8"
                  >
                    <div className="flex-1">
                      <h4 className="font-serif text-2xl lg:text-[1.7rem] leading-snug text-[#2C2C2A] group-hover:text-[#BC472A] transition-colors">
                        {item.name}
                      </h4>
                      <p className="mt-2 text-[0.95rem] font-light leading-relaxed text-[#3a3a37]/90 max-w-2xl">
                        {tr(item.desc)}
                      </p>
                      {item.allergens && (
                        <p className="mt-2 text-[0.7rem] tracking-[0.22em] uppercase text-[#737554]">
                          * {item.allergens}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="font-serif text-xl lg:text-2xl whitespace-nowrap">
                        {item.price}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
